/*
 * Las capas HTTP que saben refrescar el token: el interceptor de axios (para
 * auth/FetchInterceptor.js, la instancia default de axios y cada AxiosRequest
 * de utils/axios-request.js) y fetchConSesion (para urql y cualquier fetch a
 * pelo). Ante 401 TOKEN_EXPIRED refrescan una vez y reintentan; cualquier otro
 * code de sesión lo entregan a authClient.recibirCodigo, que decide si es
 * sesión muerta (SESSION_*, BLACKLISTED), servicio caído (SESSION_UNAVAILABLE)
 * o nada.
 *
 * A diferencia de levita-front, aquí un 401 SIN code es un error normal de la
 * petición (spec fase 3 §6.1): mini-api responde 401 UNAUTHORIZED a tokens
 * inválidos y no debe cerrar la sesión.
 *
 * El refresco va con { forzar: true }: un 401 TOKEN_EXPIRED significa que el
 * server ya rechazó el token, así que la guarda de frescura del cliente (que
 * mira el reloj local) no debe saltarse la petición. Si el refresco falla no
 * hay reintento: se propaga el 401 original.
 */
import authClient from './authClient';

export const TOKEN_EXPIRED = 'TOKEN_EXPIRED';

// Una respuesta 401/403/503 puede llegar resuelta o rechazada (validateStatus).
export const ESTADOS_CON_CODE = [401, 403, 503];

// Devuelve el code y el message que mandó el server: BLACKLISTED se le muestra
// al usuario con ese texto (spec fase 3 §3.4), así que los dos viajan juntos.
export function datosDeError(err) {
  const data = err && err.response && err.response.data;
  if (data && typeof data.code === 'string')
    return { code: data.code, message: data.message };
  return { code: '', message: undefined };
}

// El interceptor nunca pone un Authorization que no estuviera: los PUT
// prefirmados de S3 también salen por axios y no llevan token. La búsqueda es
// insensible a mayúsculas y reemplaza la clave tal cual venía.
function claveAuthorization(headers) {
  if (!headers) return '';
  return (
    Object.keys(headers).find((k) => {
      return k.toLowerCase() === 'authorization';
    }) || ''
  );
}

function conAuthorization(headers, token) {
  const clave = claveAuthorization(headers);
  if (!clave) return headers;
  return { ...headers, [clave]: token };
}

function leerTexto(blob) {
  if (typeof blob.text === 'function') return blob.text();
  return new Promise((resolve) => {
    try {
      const lector = new FileReader();
      lector.onload = () => {
        return resolve(String(lector.result || ''));
      };
      lector.onerror = () => {
        return resolve('');
      };
      lector.readAsText(blob);
    } catch (e) {
      resolve('');
    }
  });
}

function decodificar(buffer) {
  if (typeof TextDecoder !== 'undefined')
    return new TextDecoder().decode(buffer);
  return String.fromCharCode.apply(null, new Uint8Array(buffer));
}

// Un blob/arraybuffer de error (responseType 'blob' en las descargas) esconde el
// JSON con el code: sin leerlo, un SESSION_REVOKED durante una descarga pasa
// como un fallo cualquiera.
export async function normalizarCuerpo(response) {
  if (!response || !ESTADOS_CON_CODE.includes(response.status)) return response;
  const datos = response.data;
  let texto = null;
  try {
    if (typeof Blob !== 'undefined' && datos instanceof Blob)
      texto = await leerTexto(datos);
    else if (typeof ArrayBuffer !== 'undefined' && datos instanceof ArrayBuffer)
      texto = decodificar(datos);
  } catch (e) {
    texto = null;
  }
  if (!texto) return response;
  try {
    const parseado = JSON.parse(texto);
    if (parseado && typeof parseado === 'object') response.data = parseado;
  } catch (e) {
    // no era JSON: se queda el blob tal cual
  }
  return response;
}

function debeRefrescar(response, config) {
  return (
    !!response &&
    response.status === 401 &&
    datosDeError({ response }).code === TOKEN_EXPIRED &&
    !!config &&
    !config.__reintentado
  );
}

// Marca de "no hubo reintento". Es un valor propio y no `null`/`undefined`
// porque lo que devuelve el reintento puede ser CUALQUIER cosa: el interceptor
// de auth/FetchInterceptor.js desenvuelve la respuesta a `response.data`, y ese
// payload puede ser '', 0, false o null. Con una comprobación por truthiness,
// un reintento correcto con cuerpo vacío se leía como "no se reintentó" y se
// devolvía el 401 original.
const SIN_REINTENTO = Symbol('sin-reintento');

// Refresca y reenvía la petición una sola vez. Devuelve SIN_REINTENTO si no
// hubo token: cada handler decide entonces qué hacer con la respuesta/el error
// original.
async function refrescarYReintentar(instance, cliente, config) {
  let token = '';
  try {
    token = await cliente.refresh({ forzar: true });
  } catch (e) {
    token = '';
  }
  if (!token) return SIN_REINTENTO;
  config.__reintentado = true;
  config.headers = conAuthorization(config.headers, token);
  return instance.request(config);
}

function entregarSiEsDeSesion(cliente, response) {
  const { code, message } = datosDeError({ response });
  if (code && response && ESTADOS_CON_CODE.includes(response.status))
    cliente.recibirCodigo(code, message);
}

export function instalarRefrescoAxios(instance, cliente = authClient) {
  instance.interceptors.response.use(
    async (response) => {
      await normalizarCuerpo(response);
      const { config } = response || {};
      if (debeRefrescar(response, config)) {
        const reintento = await refrescarYReintentar(instance, cliente, config);
        if (reintento !== SIN_REINTENTO) return reintento;
        return response;
      }
      entregarSiEsDeSesion(cliente, response);
      return response;
    },
    async (error) => {
      const { config, response } = error || {};
      await normalizarCuerpo(response);
      if (debeRefrescar(response, config)) {
        const reintento = await refrescarYReintentar(instance, cliente, config);
        if (reintento !== SIN_REINTENTO) return reintento;
      } else {
        entregarSiEsDeSesion(cliente, response);
      }
      throw error;
    }
  );
  return instance;
}

function cabecerasCon(init, clave, token) {
  return { ...init, headers: { ...init.headers, [clave]: token } };
}

async function codigoDeRespuesta(respuesta) {
  if (!respuesta || !ESTADOS_CON_CODE.includes(respuesta.status))
    return { code: '', message: undefined };
  try {
    const copia =
      typeof respuesta.clone === 'function' ? respuesta.clone() : respuesta;
    const datos = await copia.json();
    if (datos && typeof datos.code === 'string')
      return { code: datos.code, message: datos.message };
  } catch (e) {
    // cuerpo vacío, HTML o binario: no hay code que entregar
  }
  return { code: '', message: undefined };
}

// Mismo trato que el interceptor de axios para quien usa fetch a pelo (urql y
// las descargas): siempre devuelve la Response final, quien llama decide. Nunca
// añade un Authorization que quien llama no puso: hay descargas firmadas que no
// llevan sesión.
export async function fetchConSesion(url, init = {}, cliente = authClient) {
  const clave = claveAuthorization(init.headers);
  const conSesion = (token) => {
    return clave ? cabecerasCon(init, clave, token) : init;
  };
  let respuesta = await window.fetch(url, conSesion(cliente.getAccessToken()));
  let { code, message } = await codigoDeRespuesta(respuesta);
  if (respuesta.status === 401 && code === TOKEN_EXPIRED) {
    let token = '';
    try {
      token = await cliente.refresh({ forzar: true });
    } catch (e) {
      token = '';
    }
    if (token) {
      respuesta = await window.fetch(url, conSesion(token));
      ({ code, message } = await codigoDeRespuesta(respuesta));
    }
  }
  if (code && ESTADOS_CON_CODE.includes(respuesta.status))
    cliente.recibirCodigo(code, message);
  return respuesta;
}
